import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

// ErrorBoundary is a class component and cannot use hooks.
// Use a simple translation map keyed by navigator.language for i18n.
const EB_STRINGS: Record<string, Record<string, string>> = {
  da: {
    title: 'Noget gik galt',
    description: 'Der opstod en uventet fejl. Prøv at genindlæse siden.',
    goHome: 'Gå til forsiden',
    reload: 'Genindlæs siden',
    technicalDetails: 'Tekniske detaljer',
  },
  en: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Try reloading the page.',
    goHome: 'Go to home',
    reload: 'Reload page',
    technicalDetails: 'Technical details',
  },
};

function getEBStrings(): Record<string, string> {
  const lang = navigator.language?.startsWith('da') ? 'da' : 'en';
  return EB_STRINGS[lang];
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryClass extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const s = getEBStrings();

      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4" role="alert">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {s.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              {s.description}
            </p>
            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition"
              >
                {s.goHome}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                {s.reload}
              </button>
            </div>
            {this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                  {s.technicalDetails}
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-900 rounded-lg text-xs text-red-600 dark:text-red-400 overflow-auto max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryClass;
